import {
  KoButton,
  KoDrawer,
  KoForm,
  KoLoader,
  KoModal,
  KoMonaco,
  KoTable,
  KoText,
  KoTitle,
  KoTag,
  KoToast,
  KoEmpty,
  KoPagination
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import { ApiPlatformAPI } from '../services/apiplatform.service';
import { COLUMNS_ENDPOINTS, HTTP_METHOD_CONFIG, AUTH_TYPE_CONFIG, ENDPOINT_STATUS_CONFIG } from '../constants';
import EndpointCrud from './crud/EndpointCrud';
import ApiSubNav from './ApiSubNav';
import styles from './apiplatform.module.scss';

const MOCK_ENDPOINTS = [
  { _id: 'ep-001', path: '/v3/users', method: 'GET', serviceName: 'User Service', serviceId: 'svc-001', summary: 'List all users with pagination and filtering', status: 'active', authType: 'bearer', version: 'v3', avgResponseTime: 45 },
  { _id: 'ep-002', path: '/v3/users/:id', method: 'GET', serviceName: 'User Service', serviceId: 'svc-001', summary: 'Get user by ID', status: 'active', authType: 'bearer', version: 'v3', avgResponseTime: 28 },
  { _id: 'ep-003', path: '/v3/users', method: 'POST', serviceName: 'User Service', serviceId: 'svc-001', summary: 'Create a new user account', status: 'active', authType: 'bearer', version: 'v3', avgResponseTime: 120 },
  { _id: 'ep-004', path: '/v4/payments/charge', method: 'POST', serviceName: 'Payment Gateway', serviceId: 'svc-002', summary: 'Process a payment charge with idempotency', status: 'active', authType: 'apiKey', version: 'v4', avgResponseTime: 320 },
  { _id: 'ep-005', path: '/v4/payments/refund', method: 'POST', serviceName: 'Payment Gateway', serviceId: 'svc-002', summary: 'Issue a refund for a completed payment', status: 'active', authType: 'apiKey', version: 'v4', avgResponseTime: 250 },
  { _id: 'ep-006', path: '/v4/payments/subscriptions', method: 'GET', serviceName: 'Payment Gateway', serviceId: 'svc-002', summary: 'List active subscriptions', status: 'active', authType: 'bearer', version: 'v4', avgResponseTime: 85 },
  { _id: 'ep-007', path: '/v1/notifications/send', method: 'POST', serviceName: 'Notification Hub', serviceId: 'svc-003', summary: 'Send notification via any configured channel', status: 'active', authType: 'bearer', version: 'v1', avgResponseTime: 15 },
  { _id: 'ep-008', path: '/v2/search/query', method: 'POST', serviceName: 'Search Engine', serviceId: 'svc-004', summary: 'Full-text search with filters and facets', status: 'active', authType: 'bearer', version: 'v2', avgResponseTime: 180 },
  { _id: 'ep-009', path: '/v1/billing/invoices', method: 'GET', serviceName: 'Legacy Billing (v1)', serviceId: 'svc-007', summary: 'List invoices — DEPRECATED, use Payment Gateway', status: 'deprecated', authType: 'basic', version: 'v1', avgResponseTime: 450 },
  { _id: 'ep-010', path: '/v1/files/upload', method: 'POST', serviceName: 'File Storage', serviceId: 'svc-006', summary: 'Upload file with automatic CDN distribution', status: 'active', authType: 'bearer', version: 'v1', avgResponseTime: 890 }
];

const MOCK_EP_SERVICE_LIST = [
  { label: 'User Service', value: 'svc-001' },
  { label: 'Payment Gateway', value: 'svc-002' },
  { label: 'Notification Hub', value: 'svc-003' },
  { label: 'Search Engine', value: 'svc-004' },
  { label: 'Analytics Pipeline', value: 'svc-005' },
  { label: 'File Storage', value: 'svc-006' },
  { label: 'Legacy Billing (v1)', value: 'svc-007' }
];

function Endpoints() {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  const [endpoints, setEndpoints] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ size: 20, page: 1 });

  const [isModalOpen, setModalOpen] = useState(false);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);

  const [serviceList, setServiceList] = useState([]);
  const [form] = KoForm.useForm();

  const basePath = workspaceId
    ? `/workspace/${workspaceId}/development/apiplatform`
    : '/apiplatform';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'API Platform', path: basePath },
      { title: 'Endpoints' }
    ]));
    fetchServiceList();
  }, [workspaceId]);

  useEffect(() => {
    fetchEndpoints();
  }, [workspaceId, pagination]);

  const fetchServiceList = () => {
    ApiPlatformAPI.getServiceList().then(
      (data) => {
        const list = (data?.data || data || []).map(s => ({ label: s.name || s.serviceName, value: s._id || s.serviceId }));
        setServiceList(list.length ? list : MOCK_EP_SERVICE_LIST);
      },
      () => setServiceList(MOCK_EP_SERVICE_LIST)
    );
  };

  const fetchEndpoints = () => {
    setLoading(true);
    ApiPlatformAPI.getEndpoints({ page: pagination.page, limit: pagination.size }).then(
      (res) => {
        const data = res?.data?.length ? res.data : MOCK_ENDPOINTS;
        setEndpoints(data);
        setTotalCount(res?.totalCount || data.length);
        setLoading(false);
      },
      () => { setEndpoints(MOCK_ENDPOINTS); setTotalCount(MOCK_ENDPOINTS.length); setLoading(false); }
    );
  };

  const handleSave = (values) => {
    setLoading(true);
    ApiPlatformAPI.createEndpoint(values, currentRecord?._id).then(
      () => {
        setLoading(false);
        setModalOpen(false);
        form.resetFields();
        setCurrentRecord(null);
        fetchEndpoints();
        KoToast.success(currentRecord ? 'Endpoint updated.' : 'Endpoint created.');
      },
      () => { setLoading(false); KoToast.error('Failed to save endpoint.'); }
    );
  };

  const handleDelete = () => {
    setLoading(true);
    ApiPlatformAPI.deleteEndpoint(currentRecord._id || currentRecord.endpointId).then(
      () => {
        setLoading(false);
        setDeleteOpen(false);
        setCurrentRecord(null);
        fetchEndpoints();
        KoToast.success('Endpoint deleted.');
      },
      () => { setLoading(false); KoToast.error('Delete failed.'); }
    );
  };

  const openDetail = (record) => {
    setSelectedEndpoint(record);
    setDrawerOpen(true);
  };

  const methodCfg = selectedEndpoint?.method ? HTTP_METHOD_CONFIG[selectedEndpoint.method] : {};

  const columnsWithActions = [
    ...COLUMNS_ENDPOINTS,
    {
      label: '', id: 'actions', key: 'actions', width: 120,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <KoButton
            iconBtn
            icon={<KoIcon name='EyeOutlined' width={14} height={14} />}
            onClick={(e) => { e.stopPropagation(); openDetail(record); }}
          />
          <KoButton
            iconBtn
            icon={<KoIcon name='EditOutlined' width={14} height={14} />}
            onClick={(e) => { e.stopPropagation(); setCurrentRecord(record); form.setFieldsValue(record); setModalOpen(true); }}
          />
          <KoButton
            iconBtn
            danger
            icon={<KoIcon name='DeleteOutlined' width={14} height={14} />}
            onClick={(e) => { e.stopPropagation(); setCurrentRecord(record); setDeleteOpen(true); }}
          />
        </div>
      )
    }
  ];

  return (
    <div className='tableCard'>
      {isLoading && <KoLoader />}
      <ApiSubNav activeKey='endpoints' />

      <KoToolBar
        title='API Endpoints'
        search={{ visible: true, placeholder: 'Search endpoints...', onChange: () => {} }}
        filter={{ visible: false, options: [] }}
        buttonList={[{
          label: 'Add Endpoint',
          type: 'primary',
          icon: <KoIcon color='#FFF' name='PlusOutlined' width={18} height={18} />,
          onClick: () => { setCurrentRecord(null); form.resetFields(); setModalOpen(true); }
        }]}
      />

      {endpoints.length === 0 && !isLoading && <KoEmpty description='No endpoints registered' />}
      {endpoints.length > 0 && (
        <>
          <KoTable maxHeight={480} dataSource={endpoints} columns={columnsWithActions} rowKey={(r) => r._id || r.endpointId} />
          <KoPagination totalCount={totalCount} pageSize={pagination.size} currentPage={pagination.page} onChange={(page, size) => setPagination({ page, size })} />
        </>
      )}

      {/* Endpoint Detail Drawer */}
      <KoDrawer
        title='Endpoint Detail'
        open={isDrawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedEndpoint(null); }}
        width={'min(700px, 95vw)'}
      >
        {selectedEndpoint && (
          <div>
            <div className={styles.drawerHeader}>
              <KoTag color={methodCfg?.color}>{methodCfg?.label || selectedEndpoint.method}</KoTag>
              <span className={styles.monoPath}>{selectedEndpoint.path}</span>
              {selectedEndpoint.status && (
                <KoTag color={ENDPOINT_STATUS_CONFIG[selectedEndpoint.status]?.color}>
                  {ENDPOINT_STATUS_CONFIG[selectedEndpoint.status]?.label}
                </KoTag>
              )}
            </div>

            {selectedEndpoint.summary && (
              <div className={styles.detailSection}>
                <div className={styles.detailLabel}>Summary</div>
                <KoText text={selectedEndpoint.summary} />
              </div>
            )}

            <div className={styles.detailMetaGrid}>
              <div className={styles.detailMetaItem}>
                <span className={styles.detailLabel}>Service</span>
                <span className={styles.detailValue}>{selectedEndpoint.serviceName || '—'}</span>
              </div>
              <div className={styles.detailMetaItem}>
                <span className={styles.detailLabel}>Auth</span>
                <span className={styles.detailValue}>{AUTH_TYPE_CONFIG[selectedEndpoint.authType]?.label || 'None'}</span>
              </div>
              <div className={styles.detailMetaItem}>
                <span className={styles.detailLabel}>Version</span>
                <span className={styles.detailValue}>{selectedEndpoint.version || '—'}</span>
              </div>
              <div className={styles.detailMetaItem}>
                <span className={styles.detailLabel}>Avg Response</span>
                <span className={styles.detailValue}>{selectedEndpoint.avgResponseTime ? `${selectedEndpoint.avgResponseTime}ms` : '—'}</span>
              </div>
            </div>

            {selectedEndpoint.description && (
              <div className={styles.detailSection}>
                <div className={styles.detailLabel}>Description</div>
                <KoText text={selectedEndpoint.description} />
              </div>
            )}

            {selectedEndpoint.requestSchema && (
              <div className={styles.detailSection}>
                <div className={styles.detailLabel}>Request Schema</div>
                <div style={{ border: '1px solid var(--border)', borderRadius: '0.6rem', overflow: 'hidden', minHeight: '16rem' }}>
                  <KoMonaco
                    language='json'
                    JSONdata={typeof selectedEndpoint.requestSchema === 'string' ? selectedEndpoint.requestSchema : JSON.stringify(selectedEndpoint.requestSchema, null, 2)}
                    options={{ readOnly: true }}
                  />
                </div>
              </div>
            )}

            {selectedEndpoint.responseSchema && (
              <div className={styles.detailSection}>
                <div className={styles.detailLabel}>Response Schema</div>
                <div style={{ border: '1px solid var(--border)', borderRadius: '0.6rem', overflow: 'hidden', minHeight: '16rem' }}>
                  <KoMonaco
                    language='json'
                    JSONdata={typeof selectedEndpoint.responseSchema === 'string' ? selectedEndpoint.responseSchema : JSON.stringify(selectedEndpoint.responseSchema, null, 2)}
                    options={{ readOnly: true }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </KoDrawer>

      {/* Create/Edit Modal */}
      <KoModal
        title={currentRecord ? 'Edit Endpoint' : 'Add Endpoint'}
        centered
        open={isModalOpen}
        handleOk={() => setModalOpen(false)}
        onClose={() => { setModalOpen(false); setCurrentRecord(null); }}
        width={'min(740px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => { setModalOpen(false); setCurrentRecord(null); }} />,
          <KoButton key='save' type='primary' loading={isLoading} label='Save' onClick={() => form.submit()} />
        ]}
      >
        <EndpointCrud form={form} handleFinish={handleSave} services={serviceList} />
      </KoModal>

      {/* Delete Confirmation */}
      <KoModal
        title='Delete Endpoint'
        centered
        open={isDeleteOpen}
        handleOk={() => setDeleteOpen(false)}
        onClose={() => { setDeleteOpen(false); setCurrentRecord(null); }}
        width={'min(440px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => { setDeleteOpen(false); setCurrentRecord(null); }} />,
          <KoButton key='delete' type='primary' danger loading={isLoading} label='Delete' onClick={handleDelete} />
        ]}
      >
        <div className={styles.confirmBody}>
          <KoTitle level={5} text={`Are you sure you want to delete "${currentRecord?.path}"?`} />
          <KoText text='This action cannot be undone.' />
        </div>
      </KoModal>
    </div>
  );
}

export default Endpoints;
